import React, { useEffect, useMemo, useRef, useState } from "react";
import { X, Plus } from "lucide-react";
import { INDIAN_CUISINES } from "../lib/cuisines";

/**
 * Multi-tag autocomplete input for cuisines.
 *
 * Stores the value as a comma-separated string in form state (`value` prop)
 * so it slots straight into existing `cuisine` text fields without backend
 * schema changes. Pass `onChange(string)`.
 */
export default function CuisineTagInput({ value, onChange, label = "Cuisines", testid = "cuisine-tags", suggestions = INDIAN_CUISINES, dark = false }) {
  const tags = useMemo(() => (value || "").split(",").map((t) => t.trim()).filter(Boolean), [value]);
  const [draft, setDraft] = useState("");
  const [focused, setFocused] = useState(false);
  const wrapRef = useRef(null);

  const setTags = (next) => onChange(next.join(", "));

  const matches = useMemo(() => {
    const q = draft.trim().toLowerCase();
    if (!q) return [];
    const lowerSet = new Set(tags.map((t) => t.toLowerCase()));
    return suggestions
      .filter((s) => s.toLowerCase().includes(q) && !lowerSet.has(s.toLowerCase()))
      .slice(0, 8);
  }, [draft, suggestions, tags]);

  // close suggestion dropdown on outside click
  useEffect(() => {
    if (!focused) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setFocused(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [focused]);

  const addTag = (raw) => {
    const t = (raw || "").trim().replace(/^,+|,+$/g, "");
    if (!t) return;
    if (tags.some((x) => x.toLowerCase() === t.toLowerCase())) return;
    setTags([...tags, t]);
    setDraft("");
  };
  const removeTag = (i) => setTags(tags.filter((_, x) => x !== i));

  const onKey = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag(draft);
    } else if (e.key === "Backspace" && !draft && tags.length) {
      removeTag(tags.length - 1);
    }
  };

  const dotColor = dark ? "#E5A93C" : "var(--accent, #E5A93C)";
  const inputBg = dark ? "#0F1115" : "var(--bg-muted, #121418)";
  const borderColor = dark ? "rgba(255,255,255,0.1)" : "var(--border, rgba(255,255,255,0.1))";

  return (
    <div ref={wrapRef} className="relative">
      {label && <label className="block text-xs uppercase tracking-[0.2em] font-mono mb-1.5" style={{ color: "var(--fg-muted, rgba(255,255,255,0.5))" }}>{label}</label>}
      <div
        className="rounded-xl px-3 py-2 flex flex-wrap items-center gap-1.5 min-h-[44px] cursor-text"
        style={{ background: inputBg, border: `1px solid ${borderColor}` }}
        onClick={() => wrapRef.current?.querySelector("input")?.focus()}
        data-testid={testid}
      >
        {tags.map((t, i) => (
          <span
            key={`${t}-${i}`}
            data-testid={`${testid}-tag-${i}`}
            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full font-medium"
            style={{ background: "rgba(229,169,60,0.15)", color: dotColor, border: `1px solid ${dotColor}` }}
          >
            {t}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); removeTag(i); }}
              className="opacity-70 hover:opacity-100"
              data-testid={`${testid}-remove-${i}`}
              aria-label={`Remove ${t}`}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={() => setFocused(true)}
          onKeyDown={onKey}
          placeholder={tags.length === 0 ? "Type a cuisine — e.g. Hyderabadi, Pan-Asian" : ""}
          className="flex-1 min-w-[120px] bg-transparent text-sm py-1 focus:outline-none"
          data-testid={`${testid}-input`}
        />
      </div>

      {focused && matches.length > 0 && (
        <div
          className="absolute z-30 left-0 right-0 mt-1 max-h-64 overflow-y-auto rounded-xl shadow-2xl"
          style={{ background: dark ? "#0F1115" : "var(--bg-card, #121418)", border: `1px solid ${borderColor}` }}
          data-testid={`${testid}-suggestions`}
        >
          {matches.map((s) => (
            <button
              type="button"
              key={s}
              onClick={() => { addTag(s); }}
              className="w-full text-left text-sm px-3 py-2 hover:bg-white/5 flex items-center justify-between"
              data-testid={`${testid}-suggest-${s}`}
            >
              <span>{s}</span>
              <Plus className="w-3.5 h-3.5 opacity-50" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
